import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { SlideOut } from "../SlideOut/SlideOut";
import { TablePlaceholder } from "../../util/util";
import type { ProductReference } from "../../types/types.PIM";
import { ProductCardsGrid } from "./ProductCardsGrid";
import type {
  InaccessibleProductCardItem,
  ProductCardGridItem,
} from "./ProductCardsGrid";
import { isProductReferenceValueDisplayAccessible } from "../ObjectReference/utils";
import type { ReferenceDisplayAccessContext } from "../ObjectReference/utils";
import { getReferenceRestrictedMessage } from "../ObjectReference/utils";
import { getProductReferenceHref } from "../ProductReference/ProductReferenceAttributeDisplay";

export type ProductCardsMoreModalProps = {
  references: ProductReference[];
  attributeName: string;
  onClose: () => void;
  storePath: string;
  adminPath: string;
  accessOptions: ReferenceDisplayAccessContext;
};

export const ProductCardsMoreModal = ({
  references,
  attributeName,
  onClose,
  storePath,
  adminPath,
  accessOptions,
}: ProductCardsMoreModalProps) => {
  const { t } = useTranslation();
  const { isPortfolio } = accessOptions;

  const { items, inaccessibleItems } = useMemo(() => {
    const accessibleItems: ProductCardGridItem[] = [];
    const restrictedItems: InaccessibleProductCardItem[] = [];

    references.forEach((product) => {
      if (isProductReferenceValueDisplayAccessible(product, accessOptions)) {
        accessibleItems.push({
          id: product.id,
          product,
          href: getProductReferenceHref(product, {
            isPortfolio,
            storePath,
            adminPath,
          }),
        });
      } else {
        restrictedItems.push({
          id: product.id,
          product,
          message: getReferenceRestrictedMessage(product, t),
        });
      }
    });

    return {
      items: accessibleItems,
      inaccessibleItems: restrictedItems,
    };
  }, [references, accessOptions, isPortfolio, storePath, adminPath, t]);

  return (
    <SlideOut title={attributeName} show closeFlyout={onClose}>
      {!references.length ? (
        <TablePlaceholder message={t("No items to show.")} />
      ) : (
        <ProductCardsGrid items={items} inaccessibleItems={inaccessibleItems} />
      )}
    </SlideOut>
  );
};
